import {Application} from '../application/application';
import {ContainerDefinition, Container, REQUEST_ATTRIB_DOMAIN, REQUEST_ATTRIB_CONTAINER} from '../panel/cabinets';
import {ContainerPanel} from '../panel/panel';
import {Color} from '../svg/svg';
import {DataList} from '../data-source/data-list';
import {
    Visualizer,
    VisualizerConfiguration,
    VisualizerControl,
    VisualizationUpdateLevel
} from '../data-source/visualizer';
import {TypedDatumDefinition, Datum, SpatialBounds, TemporalRange, AnimationMode} from '../data-source/types';
import {createDatumPropertyDefinition} from '../data-source/table';
import {
    getDataSource,
    ObservationDataSource,
    DeprecatedColumnDefinition,
    ColumnDefinition,
    DatumPropertyDefinition
} from './observation';
import {ObservationDetailVisualizer} from './obs-id-visualizer';
import {ObservationListVisualizer} from './obs-il-visualizer';
import {ObservationStatisticalVisualizer} from './obs-is-visualizer';
import {ObservationTicketingVisualizer, TicketingCallbackDefinition} from './obs-it-visualizer';
import {ObservationHysplitVisualizer} from './obs-ih-visualizer';
import {ArcOfInfluenceConfiguration, ObservationsAoiVisualizer} from "./observations-aoi-visualizer";


export const createObservationContainerDefinition = (): ContainerDefinition => {
    return {
        build: (panel: ContainerPanel, containerId: string, application: Application): Container => {
            return new ObservationContainer(panel, containerId, application);
        },
        url: '/' + REQUEST_ATTRIB_DOMAIN + '/observation/template/container-observation/' + REQUEST_ATTRIB_CONTAINER,
        cabinets: [
            {name: 'cab0', drawers: [{name: 'record'}]},
            {name: 'cab1', drawers: [{name: 'list'}, {name: 'statistics'}]}
        ]
    }
}


export interface EnumMapEntry {
    key: string,
    values: any[],
    labels: string[],
    colors: string[]
}

export interface CallbackDefinition {
    processor: string,
    labels?: any, // translations specific to the callback
    //  definition?: { enumMap: { [key: string]: EnumMapEntry }, states?: any[], results?: any[] }
    parameters?: any
}

export interface ObservationDatum extends Datum {
    callbacks: { [key: string]: CallbackDataProperties }
}

export interface CallbackDataProperties {
    status: string,
    retry?: string
}

/**
 * attempt at generalizing the idea of having observation types doing arbitrary extra tricks. a feature block at the
 * root level of the type configuration instructs visualizer to do whatever it is.
 */
interface ObservationTypeFeatureBlock {
    weatherFeed?: string; // optional weather feed uuid for reuse across visualizers
    weatherFeedType?: string; // supplies translated probe titles and the general set of available probes
    addFeedToTableView?: boolean, // whether sensor feed data should be appended to the table view/visualizer
    // optional array of probes from the above feed type, filled on the backend
    feedProbes?: [{
        description: string;
        id: string;
        precision: number;
    }]
    arcOfInfluence?: ArcOfInfluenceConfiguration;
}


export interface ObservationTypeDefinition extends TypedDatumDefinition {
    callbacks?: CallbackDefinition[],
    properties: {
        color?: string | Color,
        hue?: number,
        icon: string
    },
    schema: any,
    keyMap: { [key: string]: EnumMapEntry },
    views: {
        [key: string]: {
            options: any,
            view: any
        } | DeprecatedObservationTableViewDefinition
    },
    visualisers: VisualizerConfiguration[],
    features?: ObservationTypeFeatureBlock
}

export interface DeprecatedObservationTableViewDefinition {
    columns: DeprecatedColumnDefinition[],
    options?: any
}

export enum ObservationFunctionality {
    list = 'list',
    export = 'export',
    statistical = 'statistical',
    ticketing = 'ticketing',
    ticketing_export = 'ticketing_export',
    hysplit = 'hysplit',
    arcOfInfluence = 'arcOfInfluence'
}


export class ObservationContainer extends Container implements VisualizerControl {

    private observationTypeDefinitionId: string;
    private propertyMap: { [key: string]: DatumPropertyDefinition };

    private detailVisualizer: ObservationDetailVisualizer;
    private listVisualiser: ObservationListVisualizer;
    private statisticalVisualiser: ObservationStatisticalVisualizer;
    private ticketingVisualizer: ObservationTicketingVisualizer;
    private hysplitVisualizer: ObservationHysplitVisualizer;
    private arcOfInfluenceVisualizer: Visualizer;


    constructor(panel: ContainerPanel, containerId: string, app: Application) {
        super(panel, containerId, app);

    }

    /**
     * This one gets called once the skeleton markup is available
     */
    protected finishContainerBuild(): Promise<Container> {
        let observationTypeDefinition = <ObservationTypeDefinition>JSON.parse((<HTMLDivElement>this.getContainerNode().firstElementChild).dataset.definition);
        console.info('observationType=%o', observationTypeDefinition);

        let observationFunctionality = <ObservationFunctionality[]>JSON.parse((<HTMLDivElement>this.getContainerNode().firstElementChild).dataset.available);
        console.info('observationFunctionality=%o', observationFunctionality);

        this.processTypeDefinition(observationTypeDefinition, observationFunctionality);
        console.info('propertyMap=%o', this.propertyMap);

        //let our data source know that this type needs to be handled as well.
        this.observationTypeDefinitionId = getDataSource().addType(observationTypeDefinition, this);

        this.addContainerDelegatedSubscription(
            'change',
            'div.tlmd-content.settings .tlmd-viewer-settings.map .tlmd-foldout.map-viewer input',
            evAndTrgt => {
                evAndTrgt.event.preventDefault();

                let viewerSettingsContainer = (<HTMLElement>evAndTrgt.target.closest('.tlmd-viewer-settings'));
                let visualizerKey = viewerSettingsContainer.dataset.viewer;
                let visualizer: Visualizer = getDataSource().getVisualizer(visualizerKey);
                if (visualizer) {
                    if (visualizerKey === 'map') {
                        //Only the map visualizer deals with mode settings for now (20210426)
                        visualizer.refresh(VisualizationUpdateLevel.settings);
                    } else {
                        console.warn(`Unknown setting for visualizer key ${visualizerKey} in observation settings section for type ${this.observationTypeDefinitionId} found`);
                    }

                } else {
                    console.warn(`Unknown visualizer key ${visualizerKey} in observation settings section for type ${this.observationTypeDefinitionId} found`);
                }

            });

        this.detailVisualizer = <ObservationDetailVisualizer>getDataSource().addVisualizer(new ObservationDetailVisualizer('detail', this.observationTypeDefinitionId, this.getDrawer(0, 0)));
        //add ticketing visualizer when around
        if (observationFunctionality.includes(ObservationFunctionality.ticketing)) {
            this.ticketingVisualizer = <ObservationTicketingVisualizer>getDataSource().addVisualizer(new ObservationTicketingVisualizer('ticketing', this.observationTypeDefinitionId, this.getDrawer(0, 0), observationFunctionality.includes(ObservationFunctionality.ticketing_export)));
        }

        //add hysplit visualizer when around
        if (observationFunctionality.includes(ObservationFunctionality.hysplit)) {
            this.hysplitVisualizer = <ObservationHysplitVisualizer>getDataSource().addVisualizer(new ObservationHysplitVisualizer('hysplit', this.observationTypeDefinitionId, this.getDrawer(0, 0)));
        }

        //add list visualizer when around
        if (observationFunctionality.includes(ObservationFunctionality.list)) {
            this.listVisualiser = <ObservationListVisualizer>getDataSource().addVisualizer(new ObservationListVisualizer('list', this.observationTypeDefinitionId, this.getDrawer(1, 0), observationFunctionality.includes(ObservationFunctionality.export)));
        }

        //add statistics visualizer when around
        if (observationFunctionality.includes(ObservationFunctionality.statistical)) {
            this.statisticalVisualiser = <ObservationStatisticalVisualizer>getDataSource().addVisualizer(new ObservationStatisticalVisualizer('statistical', this.observationTypeDefinitionId, this.getDrawer(1, 1)));
        }

        if (observationFunctionality.includes(ObservationFunctionality.arcOfInfluence)) {
            this.arcOfInfluenceVisualizer = getDataSource()
                .addVisualizer(
                    new ObservationsAoiVisualizer(ObservationFunctionality.arcOfInfluence,
                        this.getDataSource(),
                        observationTypeDefinition));
        }

        return new Promise<Container>((resolve, _reject) => {
            resolve(this);
        });

    }

    public release(): Container {
        getDataSource().removeType(this.observationTypeDefinitionId);

        //console.trace(`releasing observation ${this.getContainerNodeSelector()} container`);
        if (this.detailVisualizer) {
            getDataSource().removeVisualizer(this.detailVisualizer.key());
            this.detailVisualizer.release();
        }
        if (this.ticketingVisualizer) {
            getDataSource().removeVisualizer(this.ticketingVisualizer.key());
            this.ticketingVisualizer.release();
        }
        if (this.hysplitVisualizer) {
            getDataSource().removeVisualizer(this.hysplitVisualizer.key());
            this.hysplitVisualizer.release();
        }
        if (this.listVisualiser) {
            getDataSource().removeVisualizer(this.listVisualiser.key());
            this.listVisualiser.release();
        }
        if (this.statisticalVisualiser) {
            getDataSource().removeVisualizer(this.statisticalVisualiser.key());
            this.statisticalVisualiser.release();
        }

        if (this.arcOfInfluenceVisualizer) {
            // no need to call release, the removeVisualizer call does all we need
            getDataSource().removeVisualizer(this.arcOfInfluenceVisualizer.key());
        }

        return super.release();
    }

    public onFocus(): void {
        let searchTerm = getDataSource().getData()[this.observationTypeDefinitionId].getSearchTerm();
		if (this.listVisualiser) {
        	this.listVisualiser.setSearchTerm(searchTerm);
		}
    }

    public onBlur(): void {
        //when the user is really quick we need to test for the existence of these data members
        if (this.listVisualiser) {
            this.listVisualiser.setSearchTerm('');
        }
        //    this.visualizationMode('map');
        if (this.observationTypeDefinitionId) {
            getDataSource().onBlur(this.observationTypeDefinitionId);
        }
        //    this.visualizationMode('map');
    }

    public setSearchTerm(searchTerm: string): void {
		if (this.listVisualiser) {
        this.listVisualiser.setSearchTerm(searchTerm ? searchTerm.trim() : '');
		}
        this.getDataSource().onSearchTerm(this.observationTypeDefinitionId, searchTerm ? searchTerm.trim() : '');
    }

    public getDefinition(): ObservationTypeDefinition {
        return <ObservationTypeDefinition>getDataSource().getDefinitions()[this.observationTypeDefinitionId];
    }

    public getPropertyMap(): { [key: string]: DatumPropertyDefinition } {
        return this.propertyMap;
    }

    public getDataSource(): ObservationDataSource {
        return getDataSource();
    }

    public getDataList(): DataList {
        return this.getDataSource().getData()[this.observationTypeDefinitionId];
    }

    public enableVisualizer(visualizerKey: string, _enable: boolean): void {
        let visualizer: Visualizer = getDataSource().getVisualizer(visualizerKey);
        if (visualizer) {
            visualizer.refresh(VisualizationUpdateLevel.settings);
        } else {
            console.warn(`Unknown visualizer key ${visualizerKey} in observation settings section for type ${this.observationTypeDefinitionId} found`);
        }
    }

    public enabled(visualizerKey: string): boolean {
        let enabler = <HTMLInputElement>this.getContainerNode().querySelector(`div.tlmd-content.settings .tlmd-viewer-settings.${visualizerKey} .tlmd-enabler input`);
        if (enabler) {
            return enabler.checked;
        } else {
            console.warn(`Visualizer key ${visualizerKey} could not be found in observation settings section for type ${this.observationTypeDefinitionId}`);
        }
    }

    public setEnabled(visualizerKey: string, enabled: boolean, className?: string): void {
        let enabler = <HTMLInputElement>this.getContainerNode().querySelector(`div.tlmd-content.settings .tlmd-viewer-settings.${visualizerKey} .tlmd-enabler input`);
        if (enabler) {
            if (visualizerKey.startsWith('hysplit')) {
                enabler.classList.remove('tlmd-gray', 'tlmd-green', 'tlmd-blue', 'tlmd-red', 'tlmd-orange', 'tlmd-yellow');
                if (!!className) {
                    enabler.classList.add(className);
                }
            }
            if (enabler.checked != enabled) {
                enabler.checked = enabled;
                this.enableVisualizer(visualizerKey, enabled);
            }
        } else {
            console.warn(`Visualizer key ${visualizerKey} could not be found in observation settings section for type ${this.observationTypeDefinitionId}`);
        }
    }

    public animationMode(visualizerKey: string): AnimationMode {
        if (visualizerKey === 'map') {
            let animationMode =
                Array.from(this.getContainerNode().querySelectorAll(`div.tlmd-content.settings .tlmd-viewer-settings.${visualizerKey} .tlmd-foldout.map-viewer input`))
                    .filter((inputElem: HTMLInputElement) => {
                        return inputElem.checked;
                    })
                    .reduce((_mode: string, inputElem: HTMLInputElement) => {
                        return inputElem.value;
                    }, AnimationMode.always);

            return (<any>AnimationMode)[animationMode];
        }

        return undefined;
    }


    private processTypeDefinition(typeDefinition: ObservationTypeDefinition, functionality: ObservationFunctionality[]): void {

        let tableViewDefinition = <DeprecatedObservationTableViewDefinition>(typeDefinition.views.table);

        if (functionality.includes(ObservationFunctionality.ticketing)) {
            let ticketingCallbackDefinition = <TicketingCallbackDefinition>typeDefinition.callbacks.filter(callbackDefinition => callbackDefinition.processor === 'ticketing')[0];

            let toEnumLabels = (definitions: { id: string, label: string }[]) =>
                definitions.reduce(
                    (map: { [key: string]: string }, definition) => {
                        map[definition.id] = definition.label;
                        return map;
                    }
                    , {});

            //inject status table column
            tableViewDefinition.columns.push(
                {
                    key: "callbacks.ticketing.result.status",
                    title: ticketingCallbackDefinition.labels.status,
                    schemaType: "string",
                    probeType: "radio",
                    visible: true,
                    probeOptions: {
                        table: {
                            sortOnLabel: true,
                            render: "auto",
                            export: true
                        }
                    },
                    renderOptions: {},
                    defaultContent: "&mdash;",
                    enumLabels: toEnumLabels(ticketingCallbackDefinition.definition.states)
                }
            );

            //inject result table column
            tableViewDefinition.columns.push(
                {
                    key: "callbacks.ticketing.result.result",
                    title: ticketingCallbackDefinition.labels.result,
                    schemaType: "string",
                    probeType: "radio",
                    visible: true,
                    probeOptions: {
                        table: {
                            sortOnLabel: true,
                            render: "auto",
                            export: true
                        }
                    },
                    renderOptions: {},
                    defaultContent: "&mdash;",
                    enumLabels: toEnumLabels(ticketingCallbackDefinition.definition.results)
                }
            );
        }

        this.propertyMap = this.processTableView(tableViewDefinition);
    }

    private processTableView(tableViewDefinition: DeprecatedObservationTableViewDefinition): {
        [key: string]: DatumPropertyDefinition
    } {

        let columns = (<DeprecatedColumnDefinition[]>tableViewDefinition.columns).map(column => {
            //convert old style definitions to new style definitions
            // fix missing parameters
            let columnDefinition: ColumnDefinition = {
                key: column.key,
                field: column.key,
                title: column.title,
                schemaType: column.schemaType,
                probeType: column.probeType,
                probeOptions: {
                    table: {
                        valueToDependentKeyMap: column.probeOptions && column.probeOptions.table
                            ? column.probeOptions.table.valueToDependentKeyMap
                            : column.renderOptions && column.renderOptions.depMap ? column.renderOptions.depMap : undefined,
                        render: column.probeOptions && column.probeOptions.table ? column.probeOptions.table.render : column.renderType && column.renderType === 'mapDependent' ? 'mapDependent' : 'auto',
                        sortOnLabel: column.enumLabels ? true : column.probeOptions ? column.probeOptions.table ? column.probeOptions.table.sortOnLabel : !(column.probeOptions.sortOnLabel === false) : column.renderOptions ? !(column.renderOptions.sortOnValue === true) : true,
                    },
                    dateFormat: column.probeOptions && column.probeOptions.dateFormat ? column.probeOptions.dateFormat : column.datetimeFormat ? column.datetimeFormat : column.probeType.toLowerCase().startsWith('datetime') ? 'L LT' : undefined
                },
                enumLabels: column.enumLabels,
                dependentColumn: column.dependentColumn != null ? (<string>column.dependentColumn).substring ? true : (<boolean>column.dependentColumn) : false,
                visible: !(column.visible === false),
                defaultContent: column.defaultContent ? column.defaultContent : "&mdash;",
                export: column.probeOptions && column.probeOptions.table ? !(column.probeOptions.table['export'] === false) : true,
                exportDateFormat: column.probeOptions && column.probeOptions.table ? column.probeOptions.table.exportDateFormat : undefined
            };
            columnDefinition.key += (columnDefinition.probeOptions.table.render === 'mapDependent' ? '-dependent' : '');
            return columnDefinition;
        });

        let independentColumns = columns.filter(column => !column.dependentColumn);
        let dependentColumnMap = columns.filter(column => column.dependentColumn).reduce((colMap, col) => {
            colMap[col.field] = col;
            return colMap;

        }, <{ [key: string]: ColumnDefinition }>{});


        let propertyMap = independentColumns.map(column => createDatumPropertyDefinition(column, dependentColumnMap))
            .reduce((propertyMap, propertyDef) => {
                propertyMap[propertyDef.column.key] = propertyDef;
                return propertyMap;

            }, <{ [key: string]: DatumPropertyDefinition }>{});

        return propertyMap;
    }

    public export(): void {
		if (this.listVisualiser) {
        this.listVisualiser.export();
		}
    }

    public panMap(geometry?: SpatialBounds): void {
        if (!geometry && this.getDataList().getSelectionSize() === 1 && this.hysplitVisualizer && this.enabled(this.hysplitVisualizer.key())) {
            //special case
            this.panMapBounds(this.hysplitVisualizer.getSpatialCoordinate());
        } else {
            super.panMap(geometry);
        }
    }

    public panTimeline(temporalRange?: TemporalRange): void {
        if (!temporalRange && this.getDataList().getSelectionSize() === 1 && this.hysplitVisualizer && this.enabled(this.hysplitVisualizer.key())) {
            //special case
            this.panTimelineRange(this.hysplitVisualizer.getTemporalCoordinate());
        } else {
            super.panTimeline(temporalRange);
        }
    }

    /**
     * When a datum gets deleted we want to autoselect a new datum. This new datum  will
     * determined by the position of the 'to-be-deleted' datum in the list visualizer. When the list
     * visualizer does not exist, the natural order will determine the next  datum to be selected.
     */
    public getAutoSelectOnDeletionId(selectionIds: string[] | string): string {
        if (selectionIds && ((<string>selectionIds).substring || ((<string[]>selectionIds).push && selectionIds.length == 1))) {
            if (this.listVisualiser) {
                return this.listVisualiser.getAutoSelectOnDeletionId(selectionIds);
            } else {
                let id = (<string>selectionIds).substring ? <string>selectionIds : (<string[]>selectionIds)[0];
                let index = this.getDataList().indexOf(id);
                if (index >= -1) {
                    if (index < this.getDataList().getSize() - 1) {
                        return this.getDataList().get(index + 1).id;
                    } else {
                        return index > 0 ? this.getDataList().get(index - 1).id : undefined;
                    }
                }
            }
        }
        return undefined;
    }
} 
